// eslint-disable-next-line no-restricted-imports
import {
  Tooltip as ChakraTooltip,
  mergeProps,
  Portal,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import { useDelayedOpen } from "~/hooks/useDelayedOpen";
import {
  OverlayDepthContext,
  trackEscapeLayer,
  useOverlayZIndex,
} from "~/hooks/useOverlayZIndex";

/**
 * The Ledger Tooltip: inverse surface, `--text-12`, 140ms delay, no arrow
 * unless asked (the `tooltip` slot recipe). It shows on hover and on focus,
 * Esc closes it, and it never holds a control: anything interactive belongs
 * in a Popover.
 */
/**
 * Lets a control inside a Tooltip's trigger replace what the tooltip says.
 * A Button with a `disabledReason` cannot open a tooltip of its own there
 * (the outer Tooltip owns the trigger), so it hands its reason up instead:
 * while it is disabled, the outer tooltip says why ("Needs a connection")
 * rather than what the control would do.
 *
 * Only controls that are inside the trigger in the DOM count: a Button in a
 * popover, menu or dialog opened from the trigger is portalled elsewhere, so
 * it keeps its own tooltip and never speaks for the trigger. Each control
 * registers under its own key, so one re-enabled control does not clear
 * another's reason.
 */
interface TooltipOverrideRegistry {
  /** Whether `node` sits inside this tooltip's trigger. */
  contains: (node: Node) => boolean;
  set: (key: string, content: React.ReactNode) => void;
  remove: (key: string) => void;
}

const TooltipOverrideContext =
  React.createContext<TooltipOverrideRegistry | null>(null);

/**
 * For a control inside a Tooltip's trigger: shows `content` in that tooltip
 * while it is set, and returns whether the control is inside such a trigger
 * (when it is not, the control shows its own tooltip).
 */
export function useTooltipOverride({
  content,
  nodeRef,
}: {
  content: React.ReactNode | null;
  nodeRef: React.RefObject<Element | null>;
}): boolean {
  const registry = React.useContext(TooltipOverrideContext);
  const key = React.useId();
  const [inside, setInside] = React.useState(false);
  // Keyed on the text (or on there being a node at all), so a reason built
  // as fresh JSX each render does not loop through the tooltip's state.
  const latest = React.useRef(content);
  latest.current = content;
  const contentKey =
    typeof content === "string" || typeof content === "number"
      ? content
      : content == null
        ? null
        : "node";
  // Where the control is can change from one render to the next (the node
  // is replaced when it stops wrapping itself in its own tooltip).
  React.useLayoutEffect(() => {
    const node = nodeRef.current;
    const next = !!registry && !!node && registry.contains(node);
    if (next !== inside) setInside(next);
  });
  React.useLayoutEffect(() => {
    if (!registry || !inside || contentKey === null) return;
    registry.set(key, latest.current);
    return () => registry.remove(key);
  }, [registry, inside, key, contentKey]);
  return inside;
}

export interface TooltipProps extends ChakraTooltip.RootProps {
  showArrow?: boolean;
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  content: React.ReactNode;
  contentProps?: ChakraTooltip.ContentProps;
  disabled?: boolean;
}

/**
 * The trigger slot between Zag's tooltip trigger and the caller's child.
 *
 * It passes the tooltip's trigger props on the way `asChild` does, except the
 * `id` when the child brings one of its own: a menu, popover or select
 * trigger wrapped in a Tooltip keeps its own id, which is how that layer
 * finds its trigger again to return focus to it when it closes. The tooltip
 * then anchors to that id instead (`Tooltip` reads it back).
 */
const TriggerSlot = React.forwardRef<HTMLElement, Record<string, unknown>>(
  function TriggerSlot(slotProps, ref) {
    const { giveId, id, ...props } = slotProps;
    const children = slotProps.children as React.ReactElement<
      Record<string, unknown>
    >;
    delete props.children;
    const childProps = children.props;
    const childRef = (childProps.ref ??
      (children as unknown as { ref?: React.Ref<HTMLElement> }).ref) as
      | React.Ref<HTMLElement>
      | undefined;
    const composedRef = React.useCallback(
      (node: HTMLElement | null) => {
        for (const r of [ref, childRef]) {
          if (typeof r === "function") r(node);
          else if (r)
            (r as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      },
      [ref, childRef],
    );
    const merged = mergeProps(props, childProps) as Record<string, unknown>;
    // As with `asChild`, an id the child sets itself wins.
    if (giveId && childProps.id === undefined) merged.id = id;
    merged.ref = composedRef;
    return React.cloneElement(children, merged);
  },
);

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  function Tooltip(props, ref) {
    const {
      showArrow,
      children,
      disabled,
      portalled = true,
      content,
      contentProps,
      portalRef,
      ...rest
    } = props;

    const { zIndex, depth } = useOverlayZIndex();
    // Zag opens on keyboard focus without its openDelay; that delay is added
    // here, so hover and focus both wait it. A caller-controlled `open` is
    // left alone.
    // The trigger, for the focus check below and the id (further down).
    const triggerRef = React.useRef<HTMLElement | null>(null);
    const stillFocused = React.useCallback(() => {
      const el = triggerRef.current;
      return !el || el.contains(document.activeElement);
    }, []);
    const delayed = useDelayedOpen<ChakraTooltip.OpenChangeDetails>({
      delayMs: rest.openDelay ?? 140,
      defaultOpen: rest.defaultOpen,
      onOpenChange: rest.onOpenChange,
      stillFocused,
    });
    const controlled = rest.open !== undefined;
    // A controlled tooltip closed by Esc, or by a layer opening above it,
    // stays closed until its caller closes it too: the caller hears the close
    // through `onOpenChange`, and may ignore it.
    const [dismissed, setDismissed] = React.useState(false);
    if (dismissed && controlled && !rest.open) setDismissed(false);
    const isOpen = controlled ? !!rest.open && !dismissed : delayed.open;
    const openControl = controlled
      ? { open: isOpen }
      : { open: delayed.open, onOpenChange: delayed.onOpenChange };
    const close = React.useRef<() => void>(() => undefined);
    close.current = () => {
      const details = { open: false } as ChakraTooltip.OpenChangeDetails;
      if (controlled) {
        setDismissed(true);
        rest.onOpenChange?.(details);
      } else {
        delayed.onOpenChange(details);
      }
    };

    // The trigger's id. Ours, unless the trigger also opens a layer of its
    // own (a menu, popover, select or combobox trigger: it says so with
    // `aria-haspopup` or the combobox role). That layer finds its trigger by
    // its own id to return focus to it when it closes, so the trigger keeps
    // that id and the tooltip anchors to it (TriggerSlot).
    const ownTriggerId = `ledger-tooltip-trigger-${React.useId()}`;
    const callerTriggerId =
      typeof rest.ids?.trigger === "string" ? rest.ids.trigger : undefined;
    const [opensLayer, setOpensLayer] = React.useState(false);
    const [layerTriggerId, setLayerTriggerId] = React.useState<string | null>(
      null,
    );
    const giveId = !!callerTriggerId || !opensLayer;
    const triggerId =
      callerTriggerId ??
      (opensLayer && layerTriggerId ? layerTriggerId : ownTriggerId);
    React.useLayoutEffect(() => {
      if (callerTriggerId) return;
      const el = triggerRef.current;
      if (!el) return;
      const layerTrigger =
        el.hasAttribute("aria-haspopup") ||
        el.getAttribute("role") === "combobox";
      if (layerTrigger !== opensLayer) setOpensLayer(layerTrigger);
      if (
        layerTrigger &&
        el.id &&
        el.id !== ownTriggerId &&
        el.id !== layerTriggerId
      ) {
        setLayerTriggerId(el.id);
      }
    });

    // Esc and the layer stack (useOverlayZIndex, "Esc: one stack, one
    // rule"). While it shows, the tooltip is a layer: Esc closes it when it
    // is the topmost one, and a menu, list or popover that opens above it
    // closes it. Zag's own Esc handling for tooltips is off; it ignored the
    // stack.
    const [layerNode, setLayerNode] = React.useState<HTMLElement | null>(null);
    const closeOnEscape = rest.closeOnEscape ?? true;

    // What controls inside the trigger asked this tooltip to say instead (a
    // disabled Button's reason; see useTooltipOverride), by control.
    // The trigger is found by id: `asChild` hands the trigger props to the
    // child, and a ref through it is not guaranteed to land.
    const [overrides, setOverrides] = React.useState<
      ReadonlyMap<string, React.ReactNode>
    >(() => new Map());
    const registry = React.useMemo<TooltipOverrideRegistry>(
      () => ({
        contains: (node) =>
          !!document.getElementById(triggerId)?.contains(node),
        set: (key, value) =>
          setOverrides((prev) => new Map(prev).set(key, value)),
        remove: (key) =>
          setOverrides((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Map(prev);
            next.delete(key);
            return next;
          }),
      }),
      [triggerId],
    );
    const firstOverride = overrides.values().next();
    const shown = firstOverride.done ? content : firstOverride.value;
    const showing = isOpen && !!shown && !disabled;

    React.useEffect(() => {
      if (!showing || !layerNode) return;
      return trackEscapeLayer({
        node: layerNode,
        passive: true,
        closeOnEscape,
        onEscape: () => close.current(),
        onCovered: () => close.current(),
      });
    }, [showing, layerNode, closeOnEscape]);

    const positionerRef = React.useCallback(
      (node: HTMLElement | null) => {
        if (node) {
          // Zag.js sets --z-index inline based on layer stack order, which
          // can place tooltips behind dialogs. Force it higher. See #2519.
          node.style.setProperty("z-index", zIndex, "important");
        }
        setLayerNode(node);
      },
      [zIndex],
    );

    if (disabled) return children;

    return (
      <ChakraTooltip.Root
        // Interactive tooltips need a close grace period so the pointer can
        // travel across the gap from the trigger into the content to reach
        // something clickable inside, like a link. Non-interactive tooltips
        // stay snappy. An explicit closeDelay from the caller still wins.
        closeDelay={rest.interactive ? 300 : 0}
        disabled={!shown}
        {...rest}
        ids={{ ...rest.ids, trigger: triggerId }}
        // Ledger: tooltips appear after 140ms (--dur-2) on hover and on focus,
        // and close at once. Zag waits openDelay for the pointer only; the
        // focus delay lives in useDelayedOpen.
        openDelay={rest.openDelay ?? 140}
        // Esc is the layer stack's (above), not Zag's tooltip's.
        closeOnEscape={false}
        {...openControl}
      >
        {/* Around the trigger, not inside it: `asChild` clones its one child,
            so the child must stay the element itself. */}
        <TooltipOverrideContext.Provider value={registry}>
          <ChakraTooltip.Trigger
            asChild={true}
            onFocus={delayed.markFocus}
            onPointerMove={delayed.markPointer}
            onPointerDown={delayed.markPointer}
          >
            <TriggerSlot giveId={giveId} ref={triggerRef}>
              {typeof children === "string" ? (
                <Text>{children}</Text>
              ) : (
                (children as React.ReactElement)
              )}
            </TriggerSlot>
          </ChakraTooltip.Trigger>
        </TooltipOverrideContext.Provider>
        <Portal disabled={!portalled} container={portalRef}>
          <ChakraTooltip.Positioner ref={positionerRef}>
            <OverlayDepthContext.Provider value={depth}>
              <ChakraTooltip.Content ref={ref} {...contentProps}>
                {showArrow && (
                  <ChakraTooltip.Arrow>
                    <ChakraTooltip.ArrowTip />
                  </ChakraTooltip.Arrow>
                )}
                {shown}
              </ChakraTooltip.Content>
            </OverlayDepthContext.Provider>
          </ChakraTooltip.Positioner>
        </Portal>
      </ChakraTooltip.Root>
    );
  },
);
